/**
 * Patrons qui se plient — logique pure (Boîte à outils, 6e → 4e).
 *
 * Chaque solide est décrit par ses faces (orientées vers l'extérieur) et un
 * arbre de pliage ; `unfold` en tire le patron, `foldNet` le replie. Programme
 * (6e BO 2025, cycle 4 BO du 5 mars 2026) : cube et pavé droit en 6e, prisme
 * droit et cylindre en 5e, pyramide et cône en 4e. Cylindre et cône sont
 * approchés par N bandes (le patron reste celui du cours : rectangle et deux
 * disques, secteur et disque).
 *
 * Défi : les 11 patrons du cube parmi les 35 hexaminos. Un assemblage de 6
 * carrés est un patron si, replié, il donne 6 faces différentes.
 */

import {
  centroid,
  dot,
  foldNet,
  normal,
  norm,
  sub,
  unfold,
  type Face,
  type NetFace,
  type P2,
  type V3,
} from '../../ui/space3d';

export type SolidId = 'cube' | 'cuboid' | 'prism' | 'cylinder' | 'pyramid' | 'cone';

export const SOLIDS: ReadonlyArray<{ id: SolidId; label: string; minRank: number }> = [
  { id: 'cube', label: 'Cube', minRank: 1 },
  { id: 'cuboid', label: 'Pavé droit', minRank: 1 },
  { id: 'prism', label: 'Prisme droit', minRank: 2 },
  { id: 'cylinder', label: 'Cylindre', minRank: 2 },
  { id: 'pyramid', label: 'Pyramide', minRank: 3 },
  { id: 'cone', label: 'Cône', minRank: 3 },
];

/** nombre de bandes d'un cylindre ou d'un cône */
export const ROUND = 36;

export interface Dims {
  /** longueur, largeur, hauteur du pavé droit */
  L: number;
  W: number;
  H: number;
}

export interface Built {
  net: NetFace[];
  /** couleur de chaque face du patron (indice de palette) */
  tones: number[];
  /** groupe lisse de chaque face du patron (bandes du cylindre, du cône) */
  smooth: Array<number | undefined>;
}

/** retourne les faces dont la normale pointe vers l'intérieur (solide convexe) */
function outward(faces: Array<{ pts: V3[]; tone: number; smooth?: number }>) {
  const c = centroid(faces.flatMap((f) => f.pts));
  for (const f of faces) if (dot(normal(f.pts), sub(centroid(f.pts), c)) < 0) f.pts.reverse();
  return faces;
}

function box(L: number, W: number, H: number, tones: readonly number[]) {
  const p = (x: number, y: number, z: number): V3 => [x * L, y * W, z * H];
  return outward([
    { pts: [p(0, 0, 0), p(1, 0, 0), p(1, 1, 0), p(0, 1, 0)], tone: tones[0]! }, // 0 dessous
    { pts: [p(0, 0, 1), p(1, 0, 1), p(1, 1, 1), p(0, 1, 1)], tone: tones[1]! }, // 1 dessus
    { pts: [p(0, 0, 0), p(1, 0, 0), p(1, 0, 1), p(0, 0, 1)], tone: tones[2]! }, // 2 devant
    { pts: [p(0, 1, 0), p(1, 1, 0), p(1, 1, 1), p(0, 1, 1)], tone: tones[3]! }, // 3 derrière
    { pts: [p(0, 0, 0), p(0, 1, 0), p(0, 1, 1), p(0, 0, 1)], tone: tones[4]! }, // 4 gauche
    { pts: [p(1, 0, 0), p(1, 1, 0), p(1, 1, 1), p(1, 0, 1)], tone: tones[5]! }, // 5 droite
  ]);
}

function build(
  faces: Array<{ pts: V3[]; tone: number; smooth?: number }>,
  order: number[],
  parent: number[],
): Built {
  const raw = unfold({ faces: faces as Face[], order, parent });
  // patron « droit » : la charnière de la première face accrochée est horizontale
  const h = raw[1]?.hinge;
  const a = h ? Math.atan2(h[1][1] - h[0][1], h[1][0] - h[0][0]) : 0;
  const c = Math.cos(-a);
  const s = Math.sin(-a);
  const rot = (p: P2): P2 => [c * p[0] - s * p[1], s * p[0] + c * p[1]];
  const net: NetFace[] = raw.map((f) => ({
    ...f,
    poly: f.poly.map(rot),
    hinge: f.hinge ? [rot(f.hinge[0]), rot(f.hinge[1])] : null,
  }));
  return {
    net,
    tones: order.map((i) => faces[i]!.tone),
    smooth: order.map((i) => faces[i]!.smooth),
  };
}

/** croix latine : dessous au centre, dessus accroché à la face de derrière */
const BOX_ORDER = [0, 2, 3, 4, 5, 1];
const BOX_PARENT = [-1, 3, 0, 0, 0, 0];

export function buildSolid(id: SolidId, dims: Dims = { L: 3, W: 2, H: 2 }): Built {
  switch (id) {
    case 'cube':
      return build(box(2, 2, 2, [0, 1, 2, 3, 4, 5]), BOX_ORDER, BOX_PARENT);
    case 'cuboid':
      // faces opposées identiques → même couleur
      return build(box(dims.L, dims.W, dims.H, [0, 0, 1, 1, 2, 2]), BOX_ORDER, BOX_PARENT);
    case 'prism': {
      // prisme droit à base triangle équilatéral, couché sur une face latérale
      const a = 2;
      const h = 3;
      const tz = (a * Math.sqrt(3)) / 2;
      const T = (y: number): [V3, V3, V3] => [
        [0, y, 0],
        [a, y, 0],
        [a / 2, y, tz],
      ];
      const [A, B, C] = T(0);
      const [A2, B2, C2] = T(h);
      const faces = outward([
        { pts: [A, B, B2, A2], tone: 1 }, // 0 face posée
        { pts: [B, C, C2, B2], tone: 2 }, // 1
        { pts: [C, A, A2, C2], tone: 3 }, // 2
        { pts: [A, B, C], tone: 0 }, // 3 base
        { pts: [A2, B2, C2], tone: 0 }, // 4 base
      ]);
      return build(faces, [0, 1, 2, 3, 4], [-1, 0, 0, 0, 0]);
    }
    case 'pyramid': {
      const a = 3;
      const H = 2.6;
      const S: V3 = [a / 2, a / 2, H];
      const q: V3[] = [
        [0, 0, 0],
        [a, 0, 0],
        [a, a, 0],
        [0, a, 0],
      ];
      const faces = outward([
        { pts: [...q], tone: 0 },
        ...q.map((p, i) => ({ pts: [p, q[(i + 1) % 4]!, S], tone: 1 + (i % 2) })),
      ]);
      return build(faces, [0, 1, 2, 3, 4], [-1, 0, 0, 0, 0]);
    }
    case 'cylinder': {
      const r = 1.2;
      const h = 3;
      const ring = (z: number) =>
        Array.from({ length: ROUND }, (_, k): V3 => {
          const t = (2 * Math.PI * k) / ROUND;
          return [r * Math.cos(t), r * Math.sin(t), z];
        });
      const lo = ring(0);
      const hi = ring(h);
      // 0 : disque du bas ; 1..N : bandes ; N+1 : disque du haut
      const faces = outward([
        { pts: [...lo], tone: 0 },
        ...lo.map((p, k) => ({
          pts: [p, lo[(k + 1) % ROUND]!, hi[(k + 1) % ROUND]!, hi[k]!],
          tone: 1,
          smooth: 1,
        })),
        { pts: [...hi], tone: 0 },
      ]);
      const k0 = 1 + ROUND / 4; // bande accrochée aux disques
      const order = [0, k0];
      const parent: number[] = Array(ROUND + 2).fill(-1) as number[];
      parent[k0] = 0;
      const half = ROUND / 2;
      for (let d = 1; d <= half; d++) {
        const right = 1 + ((k0 - 1 + d) % ROUND);
        const prevR = 1 + ((k0 - 1 + d - 1) % ROUND);
        if (!order.includes(right)) {
          order.push(right);
          parent[right] = prevR;
        }
        if (d < half) {
          const left = 1 + ((k0 - 1 - d + ROUND) % ROUND);
          const prevL = 1 + ((k0 - 1 - d + 1 + ROUND) % ROUND);
          if (!order.includes(left)) {
            order.push(left);
            parent[left] = prevL;
          }
        }
      }
      order.push(ROUND + 1);
      parent[ROUND + 1] = k0;
      return build(faces, order, parent);
    }
    case 'cone': {
      const r = 1.2;
      const H = 2.4;
      const S: V3 = [0, 0, H];
      const lo = Array.from({ length: ROUND }, (_, k): V3 => {
        const t = (2 * Math.PI * k) / ROUND;
        return [r * Math.cos(t), r * Math.sin(t), 0];
      });
      const faces = outward([
        { pts: [...lo], tone: 0 },
        ...lo.map((p, k) => ({ pts: [p, lo[(k + 1) % ROUND]!, S], tone: 1, smooth: 1 })),
      ]);
      const k0 = 1 + ROUND / 4;
      const order = [0, k0];
      const parent: number[] = Array(ROUND + 1).fill(-1) as number[];
      parent[k0] = 0;
      const half = ROUND / 2;
      for (let d = 1; d <= half; d++) {
        const right = 1 + ((k0 - 1 + d) % ROUND);
        const prevR = 1 + ((k0 - 1 + d - 1) % ROUND);
        if (!order.includes(right)) {
          order.push(right);
          parent[right] = prevR;
        }
        if (d < half) {
          const left = 1 + ((k0 - 1 - d + ROUND) % ROUND);
          const prevL = 1 + ((k0 - 1 - d + 1 + ROUND) % ROUND);
          if (!order.includes(left)) {
            order.push(left);
            parent[left] = prevL;
          }
        }
      }
      return build(faces, order, parent);
    }
  }
}

/** faces / arêtes / sommets et ce qu'il faut voir sur le patron */
export function solidFacts(id: SolidId, dims: Dims): { title: string; facts: string[] } {
  const n = (v: number) => String(v).replace('.', ',');
  switch (id) {
    case 'cube':
      return {
        title: 'Cube : 6 faces carrées identiques, 12 arêtes, 8 sommets.',
        facts: ['Chaque face touche 4 autres faces ; la sixième lui est opposée.'],
      };
    case 'cuboid': {
      const { L, W, H } = dims;
      return {
        title: 'Pavé droit : 6 faces rectangulaires, 12 arêtes, 8 sommets.',
        facts: [
          `Les faces opposées sont identiques (même couleur) : 2 faces ${n(L)} × ${n(W)}, 2 faces ${n(L)} × ${n(H)}, 2 faces ${n(W)} × ${n(H)} (en unités de longueur).`,
          'Deux côtés qui se recollent en pliant ont la même longueur.',
        ],
      };
    }
    case 'prism':
      return {
        title: 'Prisme droit : 2 bases triangulaires, 3 faces latérales rectangulaires.',
        facts: [
          '5 faces, 9 arêtes, 6 sommets.',
          'Les deux bases sont superposables ; les arêtes latérales, perpendiculaires aux bases, ont toutes la même longueur : la hauteur du prisme.',
        ],
      };
    case 'cylinder':
      return {
        title: 'Cylindre : deux disques et une surface latérale qui s’enroule.',
        facts: [
          'À plat, la surface latérale est un rectangle : sa longueur est le périmètre du disque, 2 × π × r ; sa largeur est la hauteur du cylindre.',
        ],
      };
    case 'pyramid':
      return {
        title: 'Pyramide à base carrée : 1 base, 4 faces latérales triangulaires.',
        facts: [
          '5 faces, 8 arêtes, 5 sommets.',
          'Les faces latérales se rejoignent au sommet de la pyramide ; deux côtés qui se recollent ont la même longueur.',
        ],
      };
    case 'cone':
      return {
        title: 'Cône de révolution : un disque de base et une surface latérale.',
        facts: [
          'À plat, la surface latérale est un secteur de disque : la longueur de son arc est le périmètre du disque de base.',
        ],
      };
  }
}

/* ---------- défi : les 11 patrons du cube ---------- */

/** case [colonne ; ligne] (ligne 0 en haut) */
export type Cell = readonly [number, number];

const ck = (c: Cell) => `${c[0]},${c[1]}`;
const NEI: readonly Cell[] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

export function connected(cells: readonly Cell[]): boolean {
  if (cells.length === 0) return false;
  const set = new Set(cells.map(ck));
  const seen = new Set([ck(cells[0]!)]);
  const stack = [cells[0]!];
  while (stack.length) {
    const c = stack.pop()!;
    for (const d of NEI) {
      const n: Cell = [c[0] + d[0], c[1] + d[1]];
      if (set.has(ck(n)) && !seen.has(ck(n))) {
        seen.add(ck(n));
        stack.push(n);
      }
    }
  }
  return seen.size === cells.length;
}

const square = (c: Cell): P2[] => [
  [c[0], -c[1] - 1],
  [c[0] + 1, -c[1] - 1],
  [c[0] + 1, -c[1]],
  [c[0], -c[1]],
];

/**
 * Patron de carrés (côté 1) : parcours en largeur depuis la case la plus
 * entourée ; chaque carré se plie de 90° autour du côté commun avec sa mère.
 * `cells[i]` ↔ `order[k]` : `order` donne l'indice de case de chaque face.
 */
export function squaresNet(cells: readonly Cell[]): { net: NetFace[]; order: number[] } {
  const set = new Map(cells.map((c, i) => [ck(c), i]));
  const degree = (c: Cell) => NEI.filter((d) => set.has(ck([c[0] + d[0], c[1] + d[1]]))).length;
  let root = 0;
  cells.forEach((c, i) => {
    if (degree(c) > degree(cells[root]!)) root = i;
  });
  const order = [root];
  const net: NetFace[] = [{ poly: square(cells[root]!), parent: -1, hinge: null, angle: 0 }];
  const idx = new Map([[root, 0]]);
  for (let k = 0; k < order.length; k++) {
    const c = cells[order[k]!]!;
    for (const d of NEI) {
      const j = set.get(ck([c[0] + d[0], c[1] + d[1]]));
      if (j === undefined || idx.has(j)) continue;
      // côté commun
      const hinge: [P2, P2] =
        d[0] === 1
          ? [
              [c[0] + 1, -c[1] - 1],
              [c[0] + 1, -c[1]],
            ]
          : d[0] === -1
            ? [
                [c[0], -c[1] - 1],
                [c[0], -c[1]],
              ]
            : d[1] === 1
              ? [
                  [c[0], -c[1] - 1],
                  [c[0] + 1, -c[1] - 1],
                ]
              : [
                  [c[0], -c[1]],
                  [c[0] + 1, -c[1]],
                ];
      idx.set(j, net.length);
      order.push(j);
      net.push({ poly: square(cells[j]!), parent: k, hinge, angle: Math.PI / 2 });
    }
  }
  return { net, order };
}

export interface CubeVerdict {
  ok: boolean;
  /** paires de cases qui se superposent une fois pliées */
  overlaps: Array<[number, number]>;
}

/** 6 carrés reliés : replié, est-ce un cube (6 faces différentes) ? */
export function cubeVerdict(cells: readonly Cell[]): CubeVerdict {
  const { net, order } = squaresNet(cells);
  const folded = foldNet(net, 1);
  const centers = folded.map((f) => centroid(f));
  const overlaps: Array<[number, number]> = [];
  for (let i = 0; i < centers.length; i++)
    for (let j = i + 1; j < centers.length; j++)
      if (norm(sub(centers[i]!, centers[j]!)) < 1e-6) overlaps.push([order[i]!, order[j]!]);
  return { ok: cells.length === 6 && overlaps.length === 0, overlaps };
}

/** forme libre : la même à rotation et retournement près */
export function canonical(cells: readonly Cell[]): string {
  const forms: string[] = [];
  const maps: Array<(c: Cell) => Cell> = [
    ([x, y]) => [x, y],
    ([x, y]) => [-y, x],
    ([x, y]) => [-x, -y],
    ([x, y]) => [y, -x],
    ([x, y]) => [-x, y],
    ([x, y]) => [y, x],
    ([x, y]) => [x, -y],
    ([x, y]) => [-y, -x],
  ];
  for (const m of maps) {
    const t = cells.map(m);
    const mx = Math.min(...t.map((c) => c[0]));
    const my = Math.min(...t.map((c) => c[1]));
    forms.push(
      t
        .map((c) => [c[0] - mx, c[1] - my] as const)
        .sort((a, b) => a[1] - b[1] || a[0] - b[0])
        .map((c) => `${c[0]},${c[1]}`)
        .join(' '),
    );
  }
  return forms.sort()[0]!;
}

/** cases d'une forme canonique, ramenées en haut à gauche */
export const cellsOf = (form: string): Cell[] =>
  form.split(' ').map((s) => s.split(',').map(Number) as unknown as Cell);

/** tous les hexaminos (formes libres) : 35 */
export function hexominoes(): string[] {
  let level = new Set([canonical([[0, 0]])]);
  for (let n = 2; n <= 6; n++) {
    const next = new Set<string>();
    for (const f of level) {
      const cells = cellsOf(f);
      const have = new Set(cells.map(ck));
      for (const c of cells)
        for (const d of NEI) {
          const nc: Cell = [c[0] + d[0], c[1] + d[1]];
          if (!have.has(ck(nc))) next.add(canonical([...cells, nc]));
        }
    }
    level = next;
  }
  return [...level].sort();
}

let cubeNetsCache: string[] | null = null;
/** les 11 patrons du cube (formes libres) */
export function cubeNets(): string[] {
  cubeNetsCache ??= hexominoes().filter((f) => cubeVerdict(cellsOf(f)).ok);
  return cubeNetsCache;
}
